import {
  Timeline,
  TimelineDot,
  TimelineContent,
  TimelineConnector,
  TimelineSeparator,
} from "@mui/lab";
import { useEffect, useState } from "react";
import { LichtblickHouse } from "src/assets";
import { useTranslation } from "react-i18next";
import Modal from "src/components/Modal/Modal";
import { useNavigate } from "react-router-dom";
import { PictureAsPdf, Tick } from "src/assets";
import Text from "src/components/Displays/Text";
import { useToggle } from "src/hooks/useToggle";
import { SESSION_KEYS } from "src/consts/session";
import { SMART_CHARGING_ROUTES } from "src/consts/routes";
import PageTitle from "src/components/Displays/PageTitle";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import CopyToClipboard from "src/components/Shared/CopyToClipboard";
import ProgressBarTitle from "src/components/Displays/ProgressBarTitle";
import { CardMedia, Divider, Link, Stack, useTheme } from "@mui/material";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import { EvseSelfOnboardingDetails } from "src/models/smartChargingClientsModels";

const Intro = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("translation");

  const [isOverlayOpen, toggleIsOverlayOpen] = useToggle(false);
  const [evseCompatible, setEvseCompatible] = useState<boolean | null>(null);

  const [getEvseSelfOnboardingData] =
    useSessionStorage<EvseSelfOnboardingDetails>(
      SESSION_KEYS.EVSE_SELF_ONBOARDING_DETAILS,
    );

  const evseSelfOnboardingData = getEvseSelfOnboardingData() ?? {
    uuid: "",
    connectionUrl: "",
    isAlreadyUsed: false,
    preselectedModelName: "",
    preselectedManufacturer: "",
    onboardingGuideUrlPath: "",
    compatibilityStatus: "",
  };

  useEffect(() => {
    if (evseSelfOnboardingData.compatibilityStatus === "PREMIUM") {
      setEvseCompatible(true);
    } else {
      setEvseCompatible(false);
    }
  }, [evseSelfOnboardingData.compatibilityStatus, getEvseSelfOnboardingData]);

  const handleNextClick = () => {
    navigate(SMART_CHARGING_ROUTES.CONNECT_CONFIRM);
  };

  const handleOnboardingSlider = () => {
    navigate(SMART_CHARGING_ROUTES.ONBOARDING_SLIDER);
  };

  return (
    <>
      <Stack gap={5}>
        <Text
          text={t("page.wallboxConnect.pageTitle")}
          sx={(theme) => ({
            fontSize: "32px",
            lineHeight: "35px",
            fontFamily: "isonType",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Text
          text={t("page.wallboxConnect.pageSubTitle1")}
          sx={(theme) => ({
            fontSize: "14px",
            lineHeight: "24px",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Stack
          gap={5}
          sx={{
            maxWidth: { sm: "380px" },
          }}
        >
          {evseCompatible === true && (
            <Stack
              direction={"column"}
              sx={(theme) => ({
                mt: 8,
                borderRadius: 3,
                bgcolor: "#C6FFB2",
                overflow: "hidden",
                border: "1px solid #009A22",
              })}
            >
              <Stack
                direction={"row"}
                alignItems={"center"}
                sx={(theme) => ({
                  justifyContent: "space-between",
                })}
              >
                <Stack pl={3} gap={2} pb={10} pt={2}>
                  <Text
                    sx={(theme) => ({
                      color: "#222",
                      fontSize: "12px",
                      fontWeight: "400",
                      lineHeight: "18px",
                    })}
                    text={t("page.wallboxCompatibility.selectedWallbox")}
                  />

                  <Text
                    sx={{
                      color: "#1B5F40",
                      fontSize: "24px",
                      fontWeight: "700",
                      lineHeight: "28px",
                    }}
                    text={`${evseSelfOnboardingData?.preselectedManufacturer} ${evseSelfOnboardingData?.preselectedModelName}`}
                  />
                </Stack>

                <CardMedia
                  component="img"
                  alt="houseLogo"
                  sx={{
                    width: "96px",
                    marginTop: -4.5,
                    height: "100px",
                    objectFit: "contain",
                  }}
                  image={LichtblickHouse}
                />
              </Stack>

              <Stack
                justifyContent={"center"}
                alignItems={"center"}
                direction={"column"}
                gap={1}
                sx={(theme) => ({
                  m: 0.5,
                  mt: -8,
                  padding: "8px 16px",
                  borderRadius: 3,
                  backdropFilter: "blur(3px)",
                  border: "1px solid #48BD57",
                  bgcolor: "rgba(0, 154, 34, 0.64)",
                })}
              >
                <Stack direction={"row"} gap={1}>
                  <Tick fill="#FFF" />
                  <Text
                    text={`Kompatibel`}
                    sx={{
                      color: "#fff",
                      fontSize: "11px",
                      fontWeight: 700,
                      lineHeight: "15px",
                    }}
                  />
                </Stack>

                <Divider
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.6)",
                    height: "1px",
                    width: "100%",
                    my: 1,
                  }}
                />

                <Text
                  text={`Diese Wallbox wurde in unserem Lab getestet.`}
                  sx={{
                    color: "#FFF",
                    lineHeight: "15px",
                    fontSize: "11px",
                    fontWeight: 400,
                  }}
                />
              </Stack>
            </Stack>
          )}

          {evseCompatible === false && (
            <Stack
              direction={"column"}
              sx={(theme) => ({
                mt: 8,
                borderRadius: 3,
                bgcolor: "#BBEEFA",
                overflow: "hidden",
                border: "1px solid #0B526C",
              })}
            >
              <Stack
                direction={"row"}
                alignItems={"center"}
                sx={(theme) => ({
                  justifyContent: "space-between",
                })}
              >
                <Stack pl={3} gap={2} pb={10} pt={2}>
                  <Text
                    sx={(theme) => ({
                      color: "#222",
                      fontSize: "11px",
                      fontWeight: "400",
                      lineHeight: "15px",
                    })}
                    text={t("page.wallboxCompatibility.selectedWallbox")}
                  />

                  <Text
                    sx={{
                      color: "#0B526C",
                      fontSize: "20px",
                      fontWeight: "700",
                      lineHeight: "25px",
                    }}
                    text={`${evseSelfOnboardingData?.preselectedManufacturer} ${evseSelfOnboardingData?.preselectedModelName}`}
                  />
                </Stack>

                <CardMedia
                  component="img"
                  alt="houseLogo"
                  sx={{
                    width: "96px",
                    marginTop: -4.5,
                    height: "100px",
                    objectFit: "contain",
                  }}
                  image={LichtblickHouse}
                />
              </Stack>

              <Stack
                justifyContent={"center"}
                alignItems={"center"}
                direction={"column"}
                gap={1}
                sx={(theme) => ({
                  m: 0.5,
                  mt: -8,
                  padding: "8px 16px",
                  borderRadius: 3,
                  backdropFilter: "blur(3px)",
                  bgcolor: "#0B526C",
                })}
              >
                <Stack direction={"row"} gap={1}>
                  <Tick fill="#FFF" />
                  <Text
                    text={`Voraussichtlich Kompatibel`}
                    sx={{
                      color: "#FFF",
                      lineHeight: "15px",
                      fontSize: "11px",
                      fontWeight: 700,
                    }}
                  />
                </Stack>

                <Divider
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.20)",
                    height: "1px",
                    width: "100%",
                    my: 1,
                  }}
                />

                <Text
                  text={`Grundvoraussetzungen für Smart Charging vorhanden.`}
                  sx={{
                    color: "#FFF",
                    lineHeight: "15px",
                    fontSize: "11px",
                    fontWeight: 400,
                  }}
                />
              </Stack>
            </Stack>
          )}

          <Text
            text={t("page.wallboxConnect.formTitle")}
            sx={(theme) => ({
              fontSize: "36px",
              lineHeight: "35px",
              fontFamily: "isonType",
              color: theme.palette.mainFont.primary,
            })}
          />

          <Timeline
            sx={{
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
              margin: 0,
              padding: 0,
            }}
          >
            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot
                  sx={{ padding: 0, width: "8px", height: "8px" }}
                ></TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>

              <TimelineContent
                sx={{
                  paddingRight: 0,
                }}
              >
                <Stack
                  sx={{
                    padding: "12px",
                    borderRadius: "8px",
                    justifyContent: "center",
                    border: "1px solid #CCC",
                  }}
                >
                  <Text
                    sx={(theme) => ({
                      fontSize: "12px",
                      fontWeight: "336",
                      lineHeight: "16px",
                      fontFamily: "isonType",
                      color: theme.palette.mainFont.primary,
                    })}
                    text={t("page.wallboxConnect.timelineItem1")}
                  />
                </Stack>
              </TimelineContent>
            </TimelineItem>

            <TimelineItem>
              <TimelineSeparator sx={{ height: "125px" }}>
                <TimelineDot
                  sx={{ padding: 0, width: "8px", height: "8px" }}
                ></TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>

              <TimelineContent
                sx={{
                  paddingRight: 0,
                }}
              >
                <Stack
                  sx={{
                    padding: "12px",
                    borderRadius: "8px",
                    justifyContent: "center",
                    border: "1px solid #CCC",
                    gap: "10px",
                  }}
                >
                  <Text
                    sx={(theme) => ({
                      fontSize: "12px",
                      fontWeight: "336",
                      lineHeight: "16px",
                      fontFamily: "isonType",
                      color: theme.palette.mainFont.primary,
                    })}
                    text={t("page.wallboxConnect.timelineItem2")}
                  />

                  <Stack direction={"row"} alignItems={"center"} gap={1}>
                    <Text
                      sx={(theme) => ({
                        fontSize: "14px",
                        color: "#2E2E2E",
                        fontWeight: "bold",
                        fontFamily: "isonType",
                      })}
                      text={evseSelfOnboardingData?.connectionUrl}
                    />

                    <CopyToClipboard
                      id={evseSelfOnboardingData?.connectionUrl}
                      message={`URL ${evseSelfOnboardingData?.connectionUrl} copied to clipboard`}
                    />
                  </Stack>
                </Stack>
              </TimelineContent>
            </TimelineItem>

            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot
                  sx={{ padding: 0, width: "8px", height: "8px" }}
                ></TimelineDot>
                <TimelineConnector
                  sx={{
                    background:
                      "linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.00) 100%)",
                  }}
                />
              </TimelineSeparator>

              <TimelineContent
                sx={{
                  paddingRight: 0,
                }}
              >
                <Stack
                  sx={{
                    padding: "12px",
                    borderRadius: "8px",
                    justifyContent: "center",
                    border: "1px solid #CCC",
                  }}
                >
                  <Text
                    sx={(theme) => ({
                      fontSize: "12px",
                      fontWeight: "336",
                      lineHeight: "16px",
                      fontFamily: "isonType",
                      color: theme.palette.mainFont.primary,
                    })}
                    text={t("page.wallboxConnect.timelineItem3")}
                  />
                </Stack>
              </TimelineContent>
            </TimelineItem>
          </Timeline>

          <PrimaryButton
            fullWidth
            onClick={handleNextClick}
            text={t("page.wallboxConnect.nextButton")}
            sx={(theme) => ({
              fontSize: "14px",
              lineHeight: "20px",
              color: theme.palette.mainFont.primary,
            })}
          />
          {evseSelfOnboardingData &&
          evseSelfOnboardingData.preselectedManufacturer === "MENNEKES" &&
          evseSelfOnboardingData.preselectedModelName === "AMTRON 4You" ? (
            <>
              <PrimaryButton
                fullWidth
                sx={{
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: theme.palette.mainFont.primary,
                  backgroundColor: "#fff",
                  "&:hover": {
                    backgroundColor: "#fff",
                  },
                  border: "0.829px solid #2E2E2E",
                }}
                onClick={handleOnboardingSlider}
                text={t("page.wallboxConnect.helpWithSetup")}
              />
            </>
          ) : (
            <>
              {evseSelfOnboardingData.onboardingGuideUrlPath ? (
                <Link
                  sx={(theme) => ({
                    textAlign: "center",
                    fontFamily: "Inter",
                    fontStyle: "normal",
                    fontWeight: "700",
                    borderRadius: "32px",
                    "&:disabled": {
                      opacity: 0.6,
                      cursor: "not-allowed",
                      pointerEvents: "all !important",
                      color: theme.palette.primaryButton.text,
                    },
                    padding: "12px 24px 12px 24px",
                    textTransform: "none",
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: theme.palette.mainFont.primary,
                    backgroundColor: "#fff",
                    "&:hover": {
                      backgroundColor: "#fff",
                      textDecoration: "none",
                    },
                    border: "0.829px solid #2E2E2E",
                  })}
                  href={evseSelfOnboardingData.onboardingGuideUrlPath}
                  target="_blank"
                >
                  <Stack
                    gap={1}
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <PictureAsPdf />
                    {t("page.wallboxConnect.helpWithSetup")}
                  </Stack>
                </Link>
              ) : (
                <PrimaryButton
                  fullWidth
                  sx={{
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: theme.palette.mainFont.primary,
                    backgroundColor: "#fff",
                    "&:hover": {
                      backgroundColor: "#fff",
                    },
                    border: "0.829px solid #2E2E2E",
                  }}
                  onClick={toggleIsOverlayOpen}
                  text={t("page.wallboxConnect.helpWithSetup")}
                />
              )}
            </>
          )}
        </Stack>
      </Stack>

      <Modal
        title={
          <PageTitle
            color={theme.palette.grey[500]}
            text={t("page.wallboxConnect.modal.title")}
          />
        }
        isOpen={isOverlayOpen}
        onClose={toggleIsOverlayOpen}
        onSubmit={toggleIsOverlayOpen}
      >
        <Stack gap={4}>
          <Stack>
            <ProgressBarTitle
              color={theme.palette.grey[500]}
              fontWeight="bold"
              text={t("page.wallboxConnect.modal.point1Title")}
            />

            <ProgressBarTitle
              color={theme.palette.grey[500]}
              text={t("page.wallboxConnect.modal.point1Description")}
            />
          </Stack>

          <Stack>
            <ProgressBarTitle
              color={theme.palette.grey[500]}
              fontWeight="bold"
              text={t("page.wallboxConnect.modal.point2Title")}
            />

            <ProgressBarTitle
              color={theme.palette.grey[500]}
              text={t("page.wallboxConnect.modal.point2Description")}
            />
          </Stack>

          <Stack>
            <ProgressBarTitle
              color={theme.palette.grey[500]}
              fontWeight="bold"
              text={t("page.wallboxConnect.modal.point3Title")}
            />

            <ProgressBarTitle
              color={theme.palette.grey[500]}
              text={t("page.wallboxConnect.modal.point3Description")}
            />
          </Stack>
        </Stack>
      </Modal>
    </>
  );
};

export default Intro;
